import { isLoading } from "apollo/utils";
import clsx from "clsx";
import { useExtractDocs } from "context/ExtractDocsContext";
import {
  FILE_DETAILS_TABS,
  FILE_TABLE_ACTION_SEE_DETAILS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { activateBoundingBoxes } from "core/model/utils/featureFlags";
import { safeJSONParse } from "core/model/utils/strings";
import { FileDetailsTab, FileState, QueryProgress } from "core/types";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
  Tabs,
  TabsProps,
} from "ds/ui";
import { GeneralListAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { DocxViewer } from "dsl/atoms/DocxViewer";
import { PdfViewer, PdfViewerFile } from "dsl/atoms/PDFViewer";
import {
  getFileNameAndIsNewVersionAvailable,
  isDocxFileName,
} from "dsl/ecosystems/PatientFiles/utils";
import { useEffect, useState } from "react";
import { useTranslations } from "translations";
import { useCreateFileUrl } from "../useDownloadFile";
import { FileFeatureData } from "../useFilesFeature";
import { FileDetailExtractCard } from "./FileDetailExtractCard";

const useFetchMarkdownContent = (file: FileState | undefined) => {
  const { utils } = useExtractDocs();

  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [markdownProgress, setMarkdownProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const extractedDocument = file ? utils.getExtractedDocument(file) : undefined;

  const processedDocumentUri = extractedDocument?.processed_document_uri;
  const token = tokenAccessor();

  useEffect(() => {
    if (!processedDocumentUri) return;

    (async () => {
      try {
        setMarkdownProgress(QUERY_PROGRESS_PENDING);
        const response = await fetch(
          processedDocumentUri,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {},
        );
        const text = await response.text();
        if (activateBoundingBoxes) {
          const [, parsed] = safeJSONParse<{ plain_markdown: string }>(text);
          if (
            parsed &&
            typeof parsed === "object" &&
            "plain_markdown" in parsed
          ) {
            setMarkdownContent(parsed.plain_markdown);
          } else {
            setMarkdownContent(text);
          }
        } else {
          setMarkdownContent(text);
        }
        setMarkdownProgress(QUERY_PROGRESS_NOT_STARTED);
      } catch (error) {
        setMarkdownProgress(QUERY_PROGRESS_FAILED);
        console.error("error fetching markdown content", error);
      }
    })();

    return () => {
      setMarkdownContent(null);
      setMarkdownProgress(QUERY_PROGRESS_NOT_STARTED);
    };
  }, [processedDocumentUri, token]);

  return { markdownContent, markdownProgress, setMarkdownContent };
};

const useFetchSelectedFile = (
  externalId: string | undefined,
  action: GeneralListAction<FileFeatureData> | undefined,
) => {
  const { utils } = useExtractDocs();
  const [selectedFile, setSelectedFile] = useState<PdfViewerFile | null>(null);
  const [pdfLoadingProgress, setPdfLoadingProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const createFileUrl = useCreateFileUrl({
    externalId: externalId || "",
  });

  useEffect(() => {
    if (action?.type !== FILE_TABLE_ACTION_SEE_DETAILS) return;

    (async () => {
      try {
        setPdfLoadingProgress(QUERY_PROGRESS_PENDING);
        const file = action.data as FileState;
        const extractedDocument = utils.getExtractedDocument(file);
        if (extractedDocument?.source_document_uri) {
          setSelectedFile({
            fileName: extractedDocument.filename,
            id: file.id,
            uploadedAt: file.updated_at!,
            url: extractedDocument.source_document_uri,
          });
          setPdfLoadingProgress(QUERY_PROGRESS_NOT_STARTED);
          return;
        }

        const props = await getFileNameAndIsNewVersionAvailable({
          canAccessPatientData: true,
          file,
        });
        const url = await createFileUrl(file);
        if (!url) {
          throw new Error("[FileDetailsModal] File URL is undefined");
        }

        setSelectedFile({
          fileName: props.value as string,
          id: file.id,
          uploadedAt: file.updated_at!,
          url: url,
        });
        setPdfLoadingProgress(QUERY_PROGRESS_NOT_STARTED);
      } catch (error) {
        setPdfLoadingProgress(QUERY_PROGRESS_FAILED);
        console.error("error fetching file details", error);
        setSelectedFile(null);
      }
    })();

    return () => {
      setSelectedFile(null);
      setPdfLoadingProgress(QUERY_PROGRESS_NOT_STARTED);
    };
  }, [action]);

  return { pdfLoadingProgress, selectedFile, setSelectedFile };
};

const TabContent = ({
  isLoadingPdf,
  markdownContent,
  markdownProgress,
  selectedFile,
  tab,
}: {
  tab: FileDetailsTab;
  isLoadingPdf: boolean;
  selectedFile: PdfViewerFile;
  markdownProgress: QueryProgress;
  markdownContent: string | null;
}) => {
  switch (tab) {
    case FILE_DETAILS_TABS.MARKDOWN:
      switch (markdownProgress) {
        case QUERY_PROGRESS_PENDING:
          return (
            <div className="flex items-center justify-center">
              <Spinner
                color="primary"
                size="lg"
                testId="spinner-loading-markdown-content"
              />
            </div>
          );
        case QUERY_PROGRESS_FAILED:
          return null;
        default:
          return (
            <pre className="font-sans text-body-md wrap-break-word whitespace-pre-wrap">
              {markdownContent}
            </pre>
          );
      }
    default:
      return (
        <div className="flex h-full w-full flex-1">
          {isDocxFileName(selectedFile.fileName) ? (
            <DocxViewer
              isLoadingPdf={isLoadingPdf}
              selectedFile={selectedFile}
            />
          ) : (
            <PdfViewer
              isLoadingPdf={isLoadingPdf}
              selectedFile={selectedFile}
            />
          )}
        </div>
      );
  }
};

export const FileDetailsModal = ({
  action,
  externalId,
  reset,
}: {
  externalId: string | undefined;
  action: GeneralListAction<FileFeatureData> | undefined;
  reset: () => void;
}) => {
  const translations = useTranslations();
  const { utils } = useExtractDocs();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  const [selectedTab, setSelectedTab] = useState<FileDetailsTab>(
    FILE_DETAILS_TABS.ORIGINAL,
  );

  const { pdfLoadingProgress, selectedFile, setSelectedFile } =
    useFetchSelectedFile(externalId, action);
  const { markdownContent, markdownProgress, setMarkdownContent } =
    useFetchMarkdownContent(action?.data as FileState);

  if (!selectedFile) return null;

  const isLoadingPdf = isLoading(pdfLoadingProgress);
  const isFileExtracted = action?.data
    ? utils.isFileExtracted(action.data as FileState)
    : false;

  const fileDetailsTabs: TabsProps["items"] = [
    {
      content: (
        <TabContent
          isLoadingPdf={isLoadingPdf}
          markdownContent={markdownContent}
          markdownProgress={markdownProgress}
          selectedFile={selectedFile}
          tab={FILE_DETAILS_TABS.ORIGINAL}
        />
      ),
      key: FILE_DETAILS_TABS.ORIGINAL,
      title:
        translations.extractDocs.filesTabChanges.detailViewModal
          .tabOriginalDocument,
    },
    {
      content: (
        <TabContent
          isLoadingPdf={isLoadingPdf}
          markdownContent={markdownContent}
          markdownProgress={markdownProgress}
          selectedFile={selectedFile}
          tab={FILE_DETAILS_TABS.MARKDOWN}
        />
      ),
      key: FILE_DETAILS_TABS.MARKDOWN,
      title:
        translations.extractDocs.filesTabChanges.detailViewModal.markdownTab,
    },
  ];

  return (
    <Modal
      isOpen={!!selectedFile}
      onClose={() => {
        setSelectedFile(null);
        setMarkdownContent(null);
        setSelectedTab(FILE_DETAILS_TABS.ORIGINAL);
        reset();
      }}
    >
      <ModalContent className="flex h-[90vh] w-[95vw] max-w-[800px] flex-col">
        <ModalHeader
          className={clsx("flex w-full flex-col justify-between gap-4 p-4")}
        >
          <h1 className="text-heading-lg font-bold">
            {selectedFile.fileName ||
              translations.fileSection.fileListComponent.fileName}
          </h1>
          <FileDetailExtractCard
            file={action?.data as FileState}
            fileName={selectedFile.fileName}
          />
        </ModalHeader>
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex h-full flex-1 flex-col overflow-hidden p-4">
          {isFileExtracted &&
          (isDocsAndExtractActive || isDischargeLetterActive) ? (
            <Tabs
              classNames={{
                panel: "flex-1 overflow-auto",
              }}
              items={fileDetailsTabs}
              selectedKey={selectedTab}
              variant="underlined"
              onSelectionChange={(key) => setSelectedTab(key as FileDetailsTab)}
            />
          ) : (
            <TabContent
              isLoadingPdf={isLoadingPdf}
              markdownContent={markdownContent}
              markdownProgress={markdownProgress}
              selectedFile={selectedFile}
              tab={FILE_DETAILS_TABS.ORIGINAL}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
